import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import Confetti from "react-confetti"
import { playAgain, endGame } from "./justOneRoom"
import { Screen, JoButton, PlayerDot, C, F } from "./JustOneUI"
import { playSound } from "../../../utils/ballparkSounds"

const GameOverScreen = ({ room, code, playerId, isHost }) => {
  const [busy, setBusy] = useState(false)
  const myName = room.players?.[playerId]?.name || "Host"

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 500,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  })

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    playSound("gameover")
  }, [])

  const players = Object.entries(room.players || {}).sort(
    (a, b) => b[1].score - a[1].score
  )

  const handleRematch = async () => {
    setBusy(true)
    try {
      playSound("click")
      await playAgain(code, myName)
    } catch (e) {
      setBusy(false)
    }
  }

  // Cooperative Team Rating
  const teamScore = room.teamScore || 0
  const totalRounds = room.totalRounds || 7
  const percentage = totalRounds > 0 ? (teamScore / totalRounds) * 100 : 0

  let rankTitle = ""
  let rankDesc = ""
  let emoji = ""

  if (percentage === 100) {
    rankTitle = "Perfect Score!"
    rankDesc = "You are absolute mind readers. Absolutely brilliant!"
    emoji = "🏆"
  } else if (percentage >= 75) {
    rankTitle = "Masterminds!"
    rankDesc = "Superb cooperation and outstanding clue selection."
    emoji = "🌟"
  } else if (percentage >= 50) {
    rankTitle = "Great Teamwork!"
    rankDesc = "A very solid performance. You have great family chemistry!"
    emoji = "👍"
  } else {
    rankTitle = "Room to Grow!"
    rankDesc = "Some duplicate clues got in your way. Try another round!"
    emoji = "💪"
  }

  return (
    <Screen style={{ gap: 20, position: "relative", overflowX: "hidden" }}>
      {percentage >= 75 && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={200}
        />
      )}

      <div style={{ textAlign: "center", marginTop: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: C.muted, fontFamily: F.display }}>
          GAME OVER
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 44, color: C.cyan, marginTop: 4 }}>
          Final Results
        </div>
      </div>

      {/* Team score card */}
      <div style={{ textAlign: "center", padding: "20px 14px", background: C.highlight, border: `2px solid ${C.cyan}`, borderRadius: 24, boxShadow: "0 4px 10px rgba(0,0,0,0.02)" }}>
        <div style={{ fontSize: 40, marginBottom: 4 }}>{emoji}</div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 24, color: C.ink }}>
          Team Score: {teamScore} / {totalRounds}
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 16, color: C.cyanDark, marginTop: 4 }}>
          {rankTitle}
        </div>
        <div style={{ fontSize: 13, color: C.muted, fontWeight: 700, marginTop: 6, padding: "0 10px", lineHeight: 1.4 }}>
          {rankDesc}
        </div>
      </div>

      {/* Individual Leaderboard */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display, marginBottom: 8 }}>
          INDIVIDUAL LEADERBOARD
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {players.map(([pid, p], idx) => {
            const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : "";
            return (
              <div
                key={pid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: C.card,
                  border: `1.5px solid ${idx === 0 ? C.cyan : C.border}`,
                  borderRadius: 16,
                  padding: "12px 16px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                }}
              >
                <span style={{ fontWeight: 900, fontSize: 14, color: C.muted, width: 20 }}>
                  {medal || `${idx + 1}`}
                </span>
                <PlayerDot color={p.color} size={24} />
                <span style={{ fontWeight: 800, fontSize: 15, color: C.ink, flex: 1 }}>
                  {p.name} {pid === playerId && " (You)"}
                </span>
                <span style={{ fontFamily: F.display, fontWeight: 900, fontSize: 18, color: C.cyan }}>
                  {p.score} pts
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        {isHost ? (
          <JoButton onClick={handleRematch} disabled={busy} variant="cyan">
            {busy ? "Starting Rematch…" : "Play Again (Rematch)"}
          </JoButton>
        ) : (
          <div style={{ textAlign: "center", fontWeight: 700, color: C.muted, fontFamily: F.display, padding: 12 }}>
            Waiting for the host to start a rematch…
          </div>
        )}
        <Link to="/justone" style={{ textDecoration: "none", width: "100%" }}>
          <JoButton variant="panel">Leave Room</JoButton>
        </Link>
      </div>
    </Screen>
  )
}

export default GameOverScreen
